import { useCallback, useEffect, useState } from 'react';
import { LIQUIDITY_CALCULATOR_LAYOUT_BREAKPOINT_PX } from '../config/constants';

interface IUseLiquidityCalculatorStackedLayoutOptions {
    breakpoint?: number;
    enabled?: boolean;
    forceStacked?: boolean;
}

interface IUseLiquidityCalculatorStackedLayoutResult {
    layoutRef: (node: HTMLElement | null) => void;
    isStackedLayout: boolean;
}

const getObservedWidth = (entry: ResizeObserverEntry, element: HTMLElement): number =>
    entry.contentBoxSize?.[0]?.inlineSize ?? entry.contentRect.width ?? element.getBoundingClientRect().width;

export const useLiquidityCalculatorStackedLayout = ({
    breakpoint = LIQUIDITY_CALCULATOR_LAYOUT_BREAKPOINT_PX,
    enabled = true,
    forceStacked = false,
}: IUseLiquidityCalculatorStackedLayoutOptions = {}): IUseLiquidityCalculatorStackedLayoutResult => {
    const [layoutElement, setLayoutElement] = useState<HTMLElement | null>(null);
    const [isResponsiveStackedLayout, setIsResponsiveStackedLayout] = useState(false);

    const layoutRef = useCallback((node: HTMLElement | null) => setLayoutElement(node), []);

    useEffect(() => {
        if (!enabled || !layoutElement) {
            setIsResponsiveStackedLayout(false);

            return;
        }

        const updateLayout = (width: number) => {
            setIsResponsiveStackedLayout((current) => {
                const next = width < breakpoint;

                return current === next ? current : next;
            });
        };

        const observer = new ResizeObserver((entries) => {
            const entry = entries[0];
            if (!entry) return;

            updateLayout(getObservedWidth(entry, layoutElement));
        });

        observer.observe(layoutElement);
        updateLayout(layoutElement.getBoundingClientRect().width);

        return () => observer.disconnect();
    }, [breakpoint, enabled, layoutElement]);

    return { layoutRef, isStackedLayout: forceStacked || isResponsiveStackedLayout };
};
